import React, { useMemo, useCallback, forwardRef } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import InputAdornment from '@material-ui/core/InputAdornment'
import SearchIcon from '@material-ui/icons/Search'

import pesticideSchema from '@galo-monitor/forms/schemas/root/pesticide.schema'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { PesticideActions } from '@galo-monitor/stores/pesticide'
import colors from '@galo-monitor/styles/colors'
import Form from '@galo-monitor/web-components/Form'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputText from '@galo-monitor/web-components/InputText'
import RadioGroup from '@galo-monitor/web-components/RadioGroup'

import { Container } from './theme'

const PESTICIDE_TYPES = [
  'acaricide',
  'bactericidal',
  'fungicide',
  'herbicide',
  'insecticide',
  'others'
]

const FORMULATION_TYPES = [
  {
    name: 'Powder',
    type: 'soluble',
    value: 'Powder (soluble)'
  },
  {
    name: 'Powder',
    type: 'ready-to-use',
    value: 'Powder (ready to use)'
  },
  {
    name: 'Granules',
    type: 'soluble',
    value: 'Granules (soluble)'
  },
  {
    name: 'Granules',
    type: 'ready-to-use',
    value: 'Granules (ready to use)'
  },
  {
    name: 'Tablet',
    type: 'soluble',
    value: 'Tablet (soluble)'
  },
  {
    name: 'Tablet',
    type: 'ready-to-use',
    value: 'Tablet (ready to use)'
  },
  {
    name: 'Liquid',
    type: 'soluble',
    value: 'Liquid (soluble)'
  },
  {
    name: 'Liquid',
    type: 'ready-to-se',
    value: 'Liquid (ready to use)'
  }
]

const PesticideForm = forwardRef((props, formRef) => {
  const { pesticide, loading, setLoading, onSuccess, onError } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const { data: manufacturers } = useGaloMonitorApi('/admin/defensives-makers')

  const originOptions = useMemo(() => [
    {
      label: t('organic'),
      value: 'organic'
    },
    {
      label: t('chemical'),
      value: 'chemical'
    }
  ], [t])

  const typeOptions = useMemo(
    () => map(PESTICIDE_TYPES, peticideType => ({
      label: t(peticideType), value: peticideType
    })),
    [t]
  )

  const formulationOptions = useMemo(
    () => map(FORMULATION_TYPES, ({ name, type, value }) => ({
      label: t(`formulation ${ name }`, { type }),
      value
    })),
    [t]
  )

  const manufacturersOptions = useMemo(
    () => !isEmpty(manufacturers)
      ? map(manufacturers, ({ id, name }) => ({
        label: name,
        value: id.toString()
      }))
      : [],
    [manufacturers]
  )

  const handleSubmit = useCallback(
    (data) => {
      setLoading(true)

      dispatch(PesticideActions.savePesticide(
        {
          ...pesticide,
          ...data
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('pesticide', { howMany: 1 }),
            action: toLower(t(isEmpty(pesticide) ? 'created' : 'updated', { gender: 'female', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [dispatch, onError, onSuccess, pesticide, setLoading, snackbar, t]
  )

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ pesticideSchema }
      onSubmit={ handleSubmit }
    >
      <Container>
        <InputText
          name="name"
          label={ t('trade name') }
          defaultValue={ pesticide?.name ?? '' }
          disabled={ loading }
          fullWidth
        />
        <InputSelect
          name="defensive_type"
          label={ t('type', { howMany: 1 }) }
          options={ typeOptions }
          disabled={ loading }
          defaultValue={ pesticide?.defensiveType ?? '' }
          InputProps={ {
            endAdornment: (
              <InputAdornment position="end" style={ { marginRight: -25 } }>
                <SearchIcon style={ { color: colors.lightGrey4 } } />
              </InputAdornment>
            )
          } }
          creatable
          disableClearable
          fullWidth
        />
        <RadioGroup
          name="origin"
          label={ t('origin') }
          labelSize="small"
          variant="row"
          defaultValue={ pesticide?.origin ?? '' }
          options={ originOptions }
          disabled={ loading }
          fullWidth
        />
        <InputSelect
          name="formulation"
          label={ t('formulation') }
          options={ formulationOptions }
          defaultValue={ pesticide?.formulation ?? '' }
          disabled={ loading }
          creatable
          disableClearable
          fullWidth
        />
        <InputText
          name="active_ingredient"
          label={ t('active ingredient') }
          defaultValue={ pesticide?.activeIngredient ?? '' }
          disabled={ loading }
          fullWidth
        />
        <InputSelect
          name="defensives_maker_name"
          label={ t('manufacturer', { howMany: 1 }) }
          options={ manufacturersOptions }
          defaultValue={ pesticide?.defensivesMaker ?? '' }
          disabled={ loading }
          InputProps={ {
            endAdornment: (
              <InputAdornment position="end" style={ { marginRight: -25 } }>
                <SearchIcon style={ { color: colors.lightGrey4 } } />
              </InputAdornment>
            )
          } }
          creatable
          disableClearable
          fullWidth
        />
      </Container>
    </Form>
  )
})

PesticideForm.propTypes = {
  pesticide: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

PesticideForm.defaultProps = {
  pesticide: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default PesticideForm
